import { formatServicePrice } from '../utils/formatServicePrice'
import type { CatalogService } from '../hooks/usePackageCatalog'

interface SubtotalPanelProps {
  selectedIds: number[]
  byId: Map<number, CatalogService>
  /** Tipo de facturación (opcional). Por defecto mensual; define qué precio se suma. */
  billingType?: 'MONTHLY' | 'ANNUAL'
}

export function SubtotalPanel({ selectedIds, byId, billingType = 'MONTHLY' }: SubtotalPanelProps) {
  // Ignora ids que ya no estén en el catálogo vivo (selección persistida vieja).
  const selected = selectedIds
    .map((id) => byId.get(id))
    .filter((s): s is CatalogService => Boolean(s))

  const annual = billingType === 'ANNUAL'
  const priceOf = (s: CatalogService) => (annual ? s.annualPrice : s.monthlyPrice)
  const subtotal = selected.reduce((sum, s) => sum + priceOf(s), 0)
  const currencyName = selected[0]?.currencyName ?? ''
  const hasUnconfirmed = selected.some((s) => !s.hasPrice)
  const isEmpty = selected.length === 0

  return (
    <aside className="rounded-card border border-line bg-panel p-4 lg:sticky lg:top-6">
      <h3 className="text-sm font-bold text-ink">Resumen de servicios</h3>
      <p className="mt-0.5 text-xs text-ink-muted">
        {selected.length} {selected.length === 1 ? 'servicio' : 'servicios'} seleccionado
        {selected.length === 1 ? '' : 's'}
      </p>

      {isEmpty ? (
        <p className="mt-4 rounded-lg bg-surface p-3 text-xs text-ink-muted">
          Elige al menos un servicio para continuar.
        </p>
      ) : (
        <>
          <ul className="mt-4 space-y-1.5">
            {selected.map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-ink-muted">{s.name}</span>
                <span className="shrink-0 font-medium text-ink">
                  {s.hasPrice ? formatServicePrice(priceOf(s), s.currencyName) : '—'}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-center justify-between gap-2 border-t border-line pt-3">
            <span className="text-sm font-semibold text-ink">Subtotal</span>
            <span className="text-base font-bold text-brand">
              {formatServicePrice(subtotal, currencyName)}
            </span>
          </div>

          {hasUnconfirmed && (
            <p className="mt-3 text-[11px] text-ink-subtle">
              * Algunos precios están por confirmar y no se incluyen en el subtotal.
            </p>
          )}
        </>
      )}
    </aside>
  )
}
